import type { SpriteDrawable } from './scene.js';
import type { RenderCamera, RenderDecoration, RenderFrame, RenderPose, RenderPrecedence, RenderSprite } from './rendererAdapter.js';
import { cameraTranslation } from './submissionGeometry.js';

/**
 * Converts the scene representation, already ordered by the precedence graph, into the
 * renderer contract.  This deliberately lives at the view boundary: adapters
 * never need to know about scene sorting or atlas frame selection.
 */
export const spriteSubmission = (
  item: SpriteDrawable,
  camera: RenderCamera,
  level: 1 | 2 | 3,
  pose: RenderPose,
  precedence?: RenderPrecedence,
): RenderSprite => ({
  ...(precedence ? { precedence } : {}),
  // The drawable's clip path array is passed through unchanged: adapters cache clip geometry on its identity.
  ...(item.clipPath ? { clipPath: item.clipPath } : {}),
  kind: 'sprite', instanceId: item.stableId, spriteId: item.spriteId!,
  level, page: item.page ?? 0,
  frameIndex: item.frameIndex ?? 0, frameRect: [...item.rect], frameAnchor: [...item.anchor],
  // screen is the unanchored device placement in the legacy renderer.  It is
  // represented as a unit coordinate relative to the frozen camera transform.
  unitAnchor: (() => { const s = camera.state.zoom * camera.dpr, c = cameraTranslation(camera); return [(item.screen[0] - c[0]) / s, (item.screen[1] - c[1]) / s] as const; })(),
  clip: item.clip ?? null, alpha: 1, visible: true, pose,
  sort: {
    rotatedX: item.rotatedX, rotatedY: item.rotatedY,
    depth: item.rotatedX + item.rotatedY,
    height: item.sortHeight ?? item.height,
    phase: 0,
    occlusionGroup: item.occlusionGroup ?? null,
  },
});

export const renderFrame = (input: Omit<RenderFrame, 'scene'> & {
  scene: readonly SpriteDrawable[];
  poseFor: (item: SpriteDrawable) => RenderPose;
  /** Part B trace metadata per scene drawable (shared data; expanded only when a trace is written). */
  precedenceFor?: (item: SpriteDrawable) => RenderPrecedence;
  decorations?: readonly RenderDecoration[];
}): RenderFrame => {
  const decorations = new Map<string, RenderDecoration[]>();
  for (const item of input.decorations ?? []) decorations.set(item.parentId, [...(decorations.get(item.parentId) ?? []), item]);
  return {
    ...input,
    scene: input.scene.flatMap((item) => [
      spriteSubmission(item, input.camera, input.activeLevel, input.poseFor(item), input.precedenceFor?.(item)),
      ...(decorations.get(item.stableId) ?? []),
    ]),
  };
};
